<template>
    <section class="container step-indicator-container">
        <div class="step-indicator">
            <template v-for="(label, index) in labels" :key="index">
                <div class="step" :class="{ completed: current > index, active: current === index + 1 && index < labels.length - 1 }">
                    <div class="step-ring">{{ String(index + 1).padStart(2, '0') }}</div>
                    <p>{{ label }}</p>
                </div>
                <div v-if="index < labels.length - 1" class="line" :class="{ completed: current > index + 1 }"></div>
            </template>
        </div>
    </section>
</template>
<script setup lang="ts">
defineProps<{ current: number; labels: string[] }>();
</script>
<style lang="scss">
@use 'sass:color';
@import "@/styles/_variables.scss";

.step-indicator-container {
    margin-bottom: 0;
    padding-bottom: 24px;
    background: white;
}

.step-indicator {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 1.5rem 1rem 0;
    max-width: 800px;
    margin: 0 auto;
}

.step-indicator .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;
}

.step-indicator .step-ring {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 2.5px solid transparent;
    background:
        linear-gradient(white, white) padding-box,
        linear-gradient(135deg, #d1d5db, #e5e7eb) border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 13px;
    color: #9ca3af;
    transition: background 0.35s ease, transform 0.35s ease, color 0.35s ease;
}

.step-indicator .step.completed .step-ring {
    background: linear-gradient(135deg, $primary-color, color.scale($primary-color, $lightness: -15%));
    color: white;
    transform: scale(1.1);
    animation: none;
}

// active must come after completed so the animation overrides animation:none when both classes are present
.step-indicator .step.active .step-ring {
    background:
        linear-gradient(white, white) padding-box,
        linear-gradient(135deg, $primary-color, $secondary-color) border-box;
    color: $primary-color;
    border-width: 2.5px;
    transform: none;
    animation: si-breathe 1.8s ease-in-out infinite;
}

@keyframes si-breathe {
    0%   { transform: scale(1);    box-shadow: 0 0 0 0   rgba($primary-color, 0.5); }
    50%  { transform: scale(1.08); box-shadow: 0 0 0 8px rgba($primary-color, 0.15); }
    100% { transform: scale(1);    box-shadow: 0 0 0 0   rgba($primary-color, 0); }
}

.step-indicator .step p {
    margin-top: 10px;
    margin-bottom: 0;
    color: #9ca3af;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: center;
    max-width: 80px;
    transition: color 0.3s ease;
}

.step-indicator .step.active p,
.step-indicator .step.completed p {
    color: $secondary-color;
}

.step-indicator .line {
    height: 3px;
    flex: 1;
    min-width: 40px;
    background: #e5e7eb;
    margin: 22px 8px 0;
    align-self: flex-start;
    border-radius: 3px;
    position: relative;
    overflow: hidden;
}

.step-indicator .line::after {
    content: '';
    position: absolute;
    inset: 0;
    background: $primary-color;
    border-radius: 3px;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
}

.step-indicator .line.completed::after {
    transform: scaleX(1);
}
</style>